import React, { useRef, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TimePicker } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import Table from "../../../core/common/dataTable/index";
import TooltipOption from "../../../core/common/tooltipOption";
import CommonSelect from "../../../core/common/commonSelect";
import { all_routes } from "../../router/all_routes";
import {
  createClassRoutine,
  getAllClassRoutines,
  updateClassRoutine,
  deleteClassRoutine,
  type ClassRoutine,
  type ClassRoutineCreateData,
  type ClassRoutineUpdateData,
} from "../../../service/class-routine";
import { getAllClasses, Class } from "../../../service/classes";
import { getAllSections } from "../../../service/classes";
import { getAllUsers, USER } from "../../../service/user";
import { getAllCourses, Course } from "../../../service/courses";
import {
  CLASSES_SORT_OPTIONS,
  DAY_OPTIONS,
} from "../../../core/common/selectoption/selectoption";
import { useSelector } from "react-redux";
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos";
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice";

const ClassRoutineManagement = () => {
  const routes = all_routes;
  const [searchParams] = useSearchParams();
  const classIdFromUrl = searchParams.get("class");
  const componentRef = useRef<HTMLDivElement>(null);
  const systems = useSelector(selectSystemsForCurrentBranch);
  const currentBranch = useSelector(selectCurrentBranch);
  const currentSession = useSelector(selectCurrentSession);

  const [data, setData] = useState<ClassRoutine[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
  });

  const [filters, setFilters] = useState({
    class: classIdFromUrl || "",
    section: "",
    course: "",
    dayOfWeek: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    system: systems[0]?._id || "",
  });

  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [sectionOptions, setSectionOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [teacherOptions, setTeacherOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [subjectOptions, setSubjectOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [filteredSubjectOptions, setFilteredSubjectOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [sortBy, setSortBy] = useState(CLASSES_SORT_OPTIONS[0]);
  const [searchText, setSearchText] = useState("");

  const getInitialFormData = (): ClassRoutineCreateData => ({
    teacher: "",
    branch: "65d8f5a8e4b0a1c2d3e4f5a8",
    session: "65d8f5a8e4b0a1c2d3e4f5a9",
    class: classIdFromUrl || "",
    section: "",
    course: "",
    dayOfWeek: "",
    startTime: "",
    endTime: "",
    isActive: true,
    system: appliedFilters.system || systems[0]?._id || "", // ✅ System field properly set
  });

  const [formData, setFormData] = useState<ClassRoutineCreateData>(
    getInitialFormData()
  );

  const [editFormData, setEditFormData] = useState<ClassRoutineUpdateData>({});
  const [selectedRecord, setSelectedRecord] = useState<ClassRoutine | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const fetchClassesBySystem = async (systemId: string) => {
    try {
      const classesResponse = await getAllClasses({
        system: systemId,
      });
      const classOptionsData = classesResponse.classes.map((cls: Class) => ({
        value: cls._id,
        label: cls.name,
      }));
      setClassOptions(classOptionsData);
      setClasses(classesResponse.classes); // Store actual class objects for course filtering

      setFilters((prev) => ({
        ...prev,
        class: "",
      }));

      setFormData((prev) => ({
        ...prev,
        class: "",
        system: systemId,
      }));
    } catch (error) {
      console.error("Error fetching classes by system:", error);
      setClassOptions([]);
      setClasses([]);
    }
  };

  const filterCoursesByClass = async (classId: string) => {
    if (!classId) {
      setFilteredSubjectOptions(subjectOptions);
      return;
    }

    try {
      // Fetch all courses from global courses API
      const coursesResponse = await getAllCourses({});
      
      // Get class to find associated course IDs
      const selectedClass = classes.find((cls: Class) => cls._id === classId);
      const courseIds = selectedClass?.course || [];
      
      // Filter courses by class.course array
      const filteredCourses = coursesResponse.data
        .filter((course: Course) => courseIds.includes(course._id))
        .map((course: Course) => ({
          value: course._id,
          label: course.title || course.courseIdentifier,
        }));

      setFilteredSubjectOptions(filteredCourses);
    } catch (error) {
      console.error("Error filtering courses by class:", error);
      setFilteredSubjectOptions(subjectOptions);
    }
  };

  const filterCoursesForEdit = async (classId: string) => {
    if (!classId) {
      setFilteredSubjectOptions(subjectOptions);
      return;
    }

    try {
      // Fetch all courses from global courses API
      const coursesResponse = await getAllCourses({});
      
      // Get class to find associated course IDs
      const selectedClass = classes.find((cls: Class) => cls._id === classId);
      const courseIds = selectedClass?.course || [];
      
      // Filter courses by class.course array
      const filteredCourses = coursesResponse.data
        .filter((course: Course) => courseIds.includes(course._id))
        .map((course: Course) => ({
          value: course._id,
          label: course.title || course.courseIdentifier,
        }));

      setFilteredSubjectOptions(filteredCourses);
    } catch (error) {
      console.error("Error filtering courses for edit:", error);
      setFilteredSubjectOptions(subjectOptions);
    }
  };

  const fetchDropdownData = async () => {
    try {
      if (appliedFilters.system) {
        await fetchClassesBySystem(appliedFilters.system);
      }

      const sectionsResponse = await getAllSections({ session: currentSession?._id });
      const sectionOptionsData = sectionsResponse.sections.map(
        (section: any) => ({
          value: section._id,
          label: section.name,
        })
      );
      setSectionOptions(sectionOptionsData);

      const usersResponse = await getAllUsers({
        status: "active",
      });
      const teacherOptionsData = usersResponse.data
        .filter((user: USER) => {
          return (
            typeof user.role === "object" &&
            user.role !== null &&
            "name" in user.role &&
            user.role.name.toLowerCase() === "teacher"
          );
        })
        .map((user: USER) => ({
          value: user._id,
          label: `${user.firstName} ${user.lastName}`,
        }));
      setTeacherOptions(teacherOptionsData);

      const coursesResponse = await getAllCourses({});
      const subjectOptionsData = coursesResponse.data.map((course: Course) => ({
        value: course._id,
        label: course.title || course.courseIdentifier,
      }));
      setSubjectOptions(subjectOptionsData);

      if (classIdFromUrl) {
        await filterCoursesByClass(classIdFromUrl);
      } else {
        setFilteredSubjectOptions(subjectOptionsData);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load dropdown data");
    }
  };

  const fetchClassRoutines = async () => {
    try {
      setLoading(true);
      const response = await getAllClassRoutines({
        page: pagination.currentPage,
        limit: pagination.limit,
        class: filters.class,
        section: filters.section,
        course: filters.course,
        dayOfWeek: filters.dayOfWeek,
        system: appliedFilters.system,
        ...sortBy.value,
        searchText,
      });

      setData(response.data);
      setPagination(response.pagination);
    } catch (error: any) {
      console.error("Error fetching class routines:", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch class routines"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDropdownData();
  }, [appliedFilters.system, currentSession?._id]);

  useEffect(() => {
    fetchClassRoutines();
  }, [
    pagination.currentPage,
    filters,
    sortBy,
    searchText,
    appliedFilters.system,
  ]);

  useEffect(() => {
    if (classIdFromUrl) {
      setFilters((prev) => ({
        ...prev,
        class: classIdFromUrl,
      }));
      setFormData((prev) => ({
        ...prev,
        class: classIdFromUrl,
      }));
      filterCoursesByClass(classIdFromUrl);
    }
  }, [classIdFromUrl]);

  const handleSystemChange = (systemItem: any) => {
    const systemId = systemItem?._id || "";
    setAppliedFilters((prev) => ({
      ...prev,
      system: systemId,
    }));

    setFormData((prev) => ({
      ...prev,
      system: systemId,
    }));
  };

  const handleClassFilterChange = (selectedOption: any) => {
    setFilters((prev) => ({
      ...prev,
      class: selectedOption?.value || "",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const formatTimeForBackend = (timeStr: string) => {
    if (!timeStr) return "";

    if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeStr)) {
      return timeStr;
    }

    try {
      return dayjs(timeStr, "h:mm A").format("HH:mm");
    } catch (error) {
      console.error("Error formatting time:", error);
      return timeStr;
    }
  };

  const formatTimeForDisplay = (timeStr: string) => {
    if (!timeStr) return null;

    try {
      if (timeStr.includes(":")) {
        return dayjs(`2000-01-01 ${timeStr}`);
      }
      return dayjs(timeStr, "h:mm A");
    } catch (error) {
      console.error("Error parsing time:", error, timeStr);
      return null;
    }
  };

  const handleCreateClassRoutine = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);

      if (
        !formData.teacher ||
        !formData.class ||
        !formData.section ||
        !formData.course ||
        !formData.dayOfWeek ||
        !formData.startTime ||
        !formData.endTime ||
        !formData.system
      ) {
        toast.warning("Please fill all required fields");
        return;
      }

      const formattedData = {
        ...formData,
        startTime: formatTimeForBackend(formData.startTime),
        endTime: formatTimeForBackend(formData.endTime),
      };

      await createClassRoutine(formattedData);
      toast.success("Class routine created successfully");

      setFormData(getInitialFormData());

      const modal = document.getElementById("add_class_routine");
      if (modal) {
        const bootstrapModal = (window as any).bootstrap?.Modal?.getInstance(
          modal
        );
        if (bootstrapModal) {
          bootstrapModal.hide();
        } else {
          const closeButton = modal.querySelector(
            '[data-bs-dismiss="modal"]'
          ) as HTMLElement;
          if (closeButton) closeButton.click();
        }
      }

      fetchClassRoutines();
    } catch (error: any) {
      console.error("Error creating class routine:", error);
      toast.error(
        error.response?.data?.message || "Failed to create class routine"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateClassRoutine = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRecord) {
      toast.warning("No class routine selected for update");
      return;
    }

    try {
      setIsSubmitting(true);

      const updateData = { ...editFormData };
      if (updateData.startTime) {
        updateData.startTime = formatTimeForBackend(updateData.startTime);
      }
      if (updateData.endTime) {
        updateData.endTime = formatTimeForBackend(updateData.endTime);
      }

      await updateClassRoutine(selectedRecord._id, updateData);
      toast.success("Class routine updated successfully");

      setEditFormData({});
      setSelectedRecord(null);
      const modal = document.getElementById("edit_class_routine");
      if (modal) {
        const bootstrapModal = (window as any).bootstrap?.Modal?.getInstance(
          modal
        );
        if (bootstrapModal) {
          bootstrapModal.hide();
        } else {
          const closeButton = modal.querySelector(
            '[data-bs-dismiss="modal"]'
          ) as HTMLElement;
          if (closeButton) closeButton.click();
        }
      }

      fetchClassRoutines();
    } catch (error: any) {
      console.error("Error updating class routine:", error);
      toast.error(
        error.response?.data?.message || "Failed to update class routine"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClassRoutine = async () => {
    if (!selectedRecord) {
      toast.warning("No class routine selected for deletion");
      return;
    }

    try {
      await deleteClassRoutine(selectedRecord._id);
      toast.success("Class routine deleted successfully");

      const modal = document.getElementById("delete-modal");
      if (modal) {
        const bootstrapModal = (window as any).bootstrap?.Modal?.getInstance(
          modal
        );
        if (bootstrapModal) {
          bootstrapModal.hide();
        } else {
          const closeButton = modal.querySelector(
            '[data-bs-dismiss="modal"]'
          ) as HTMLElement;
          if (closeButton) closeButton.click();
        }
      }

      fetchClassRoutines();
    } catch (error: any) {
      console.error("Error deleting class routine:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete class routine"
      );
    }
  };

  const handleEditClick = async (record: ClassRoutine) => {
    try {
      setSelectedRecord(record);

      if (record.class?._id) {
        await filterCoursesForEdit(record.class._id);
      }

      setEditFormData({
        teacher: record.teacher?._id || "",
        class: record.class?._id || "",
        section: record.section?._id || "",
        course: record.course?._id || "",
        dayOfWeek: record.dayOfWeek,
        startTime: record.startTime,
        endTime: record.endTime,
        isActive: record.isActive,
      });
    } catch (error: any) {
      console.error("Error loading class routine data:", error);
      toast.error("Failed to load class routine data");
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSortSelect = (item: {
    label: string;
    value: { sortBy: string; sortOrder: string };
  }) => {
    setSortBy(item);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "class") {
      filterCoursesByClass(value);
      setFormData((prev) => ({
        ...prev,
        class: value,
        course: "",
      }));
    }
  };

  const handleEditInputChange = (field: string, value: any) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "class") {
      filterCoursesForEdit(value);
      setEditFormData((prev) => ({
        ...prev,
        class: value,
        course: "",
      }));
    }
  };

  const handleTimeChange = (
    field: "startTime" | "endTime",
    time: Dayjs | null
  ) => {
    if (time) {
      setFormData((prev) => ({
        ...prev,
        [field]: time.format("h:mm A"),
      }));
    }
  };

  const handleEditTimeChange = (
    field: "startTime" | "endTime",
    time: Dayjs | null
  ) => {
    if (time) {
      setEditFormData((prev) => ({
        ...prev,
        [field]: time.format("h:mm A"),
      }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      class: classIdFromUrl || "",
      section: "",
      course: "",
      dayOfWeek: "",
    });
  };

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
    fetchClassRoutines();
  };

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  const getModalContainer = () => document.body;
  const getModalContainer2 = () => document.body;
  const getModalContainer3 = () => document.body;
  const getModalContainer4 = () => document.body;

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      data.map((routine) => ({
        Class: routine.class?.name || "N/A",
        Section: routine.section?.name || "N/A",
        Teacher:
          routine.teacher?.firstName && routine.teacher?.lastName
            ? `${routine.teacher.firstName} ${routine.teacher.lastName}`
            : routine.teacher?.email || "N/A",
        Course: routine.course?.name || "N/A",
        Day: routine.dayOfWeek || "N/A",
        "Start Time": routine.startTime || "N/A",
        "End Time": routine.endTime || "N/A",
        "Class Room": routine.room || "N/A",
        Status: routine.isActive ? "Active" : "Inactive",
        "Created Date": new Date(routine.createdAt).toLocaleDateString(),
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Class Routines");
    XLSX.writeFile(wb, "class_routines.xlsx");
  };

  const selectedClassName =
    classOptions.find((cls) => cls.value === (filters.class || classIdFromUrl))
      ?.label || "Selected Class";

  const columns = [
    {
      title: "Class",
      dataIndex: "class",
      render: (classObj: any) => classObj?.name || "N/A",
      sorter: true,
    },
    {
      title: "Section",
      dataIndex: "section",
      render: (section: any) => section?.name || "N/A",
      sorter: true,
    },
    {
      title: "Teacher",
      dataIndex: "teacher",
      render: (teacher: any) => {
        if (!teacher) return "N/A";

        if (typeof teacher === "object" && teacher !== null) {
          const capitalizeFirstLetter = (str: string) => {
            return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
          };

          return `${capitalizeFirstLetter(
            teacher.firstName || ""
          )} ${capitalizeFirstLetter(teacher.lastName || "")}`;
        }

        const teacherOption = teacherOptions.find(
          (option) => option.value === teacher
        );
        return teacherOption?.label || "N/A";
      },
    },
    {
      title: "Course",
      dataIndex: "course",
      render: (course: any) => {
        if (!course) return "N/A";
        if (typeof course === "object" && course !== null) {
          return course.name || "N/A";
        }
        const subjectOption = subjectOptions.find(
          (option) => option.value === course
        );
        return subjectOption?.label || "N/A";
      },
      sorter: true,
    },
    {
      title: "Day",
      dataIndex: "dayOfWeek",
      sorter: true,
    },
    {
      title: "Start Time",
      dataIndex: "startTime",
      sorter: true,
    },
    {
      title: "End Time",
      dataIndex: "endTime",
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (isActive: boolean) => (
        <>
          {isActive ? (
            <span className="badge badge-soft-success d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              Active
            </span>
          ) : (
            <span className="badge badge-soft-danger d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              Inactive
            </span>
          )}
        </>
      ),
      sorter: true,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: ClassRoutine) => (
        <div className="dropdown">
          <Link
            to="#"
            className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="ti ti-dots-vertical fs-14" />
          </Link>
          <ul className="dropdown-menu dropdown-menu-right p-3">
            <li>
              <Link
                className="dropdown-item rounded-1"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#edit_class_routine"
                onClick={() => handleEditClick(record)}
              >
                <i className="ti ti-edit-circle me-2" />
                Edit
              </Link>
            </li>
            <li>
              <Link
                className="dropdown-item rounded-1"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#delete-modal"
                onClick={() => setSelectedRecord(record)}
              >
                <i className="ti ti-trash-x me-2" />
                Delete
              </Link>
            </li>
          </ul>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {classIdFromUrl || filters.class
                  ? `${selectedClassName} - Class Routine`
                  : "Class Routine"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Academic </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {classIdFromUrl || filters.class
                      ? `${selectedClassName} Routine`
                      : "All Class Routines"}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchClassRoutines}
                onExportPdf={handlePrint}
                onExportExcel={handleExportExcel}
              />
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_class_routine"
                >
                  <i className="ti ti-square-rounded-plus-filled me-2" />
                  Add Class Routine
                </Link>
              </div>
            </div>
          </div>
          {/* /Page Header */}

          {/* Class Routine List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">
                {classIdFromUrl || filters.class
                  ? `${selectedClassName} - Class Routine`
                  : "All Class Routines"}
              </h4>
              <div className="d-flex align-items-center flex-wrap">
                {/* System Dropdown */}
                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find(
                      (item: any) => item._id === appliedFilters.system
                    )}
                    getOptionValue={(item) => item._id}
                    getOptionLabel={(item) => item.name}
                    onChange={handleSystemChange}
                  />
                </div>

                {/* Class Dropdown */}
                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={classOptions}
                    value={
                      classOptions.find(
                        (option) => option.value === filters.class
                      ) || null
                    }
                    onChange={handleClassFilterChange}
                    placeholder="Select Class"
                  />
                </div>

                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom pb-0">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Section</label>
                              <CommonSelect
                                className="select"
                                options={sectionOptions}
                                value={
                                  sectionOptions.find(
                                    (option) => option.value === filters.section
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  handleFilterChange(
                                    "section",
                                    selectedOption?.value || ""
                                  )
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Subject</label>
                              <CommonSelect
                                className="select"
                                options={filteredSubjectOptions}
                                value={
                                  subjectOptions.find(
                                    (option) => option.value === filters.course
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  handleFilterChange(
                                    "course",
                                    selectedOption?.value || ""
                                  )
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Day</label>
                              <CommonSelect
                                className="select"
                                options={DAY_OPTIONS}
                                value={
                                  DAY_OPTIONS.find(
                                    (option) =>
                                      option.value === filters.dayOfWeek
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  handleFilterChange(
                                    "dayOfWeek",
                                    selectedOption?.value || ""
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {CLASSES_SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Class Routine</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                setSearchText={setSearchText}
                pagination={{
                  onChange: (page: number, pageSize?: number) => {
                    setPagination((prev) => ({
                      ...prev,
                      currentPage: page,
                      limit: pageSize || prev.limit,
                    }));
                  },
                }}
              />
            </div>
          </div>
          {/* /Class Routine List */}
        </div>
      </div>

      {/* Add Class Routine Modal */}
      <div className="modal fade" id="add_class_routine">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Class Routine</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleCreateClassRoutine}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Teacher</label>
                      <CommonSelect
                        className="select"
                        options={teacherOptions}
                        value={
                          teacherOptions.find(
                            (option) => option.value === formData.teacher
                          ) || null
                        }
                        isSearchable={true}
                        onChange={(selectedOption) =>
                          handleInputChange(
                            "teacher",
                            selectedOption?.value || ""
                          )
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Class</label>
                      <CommonSelect
                        className="select"
                        options={classOptions}
                        value={
                          classOptions.find(
                            (option) => option.value === formData.class
                          ) || null
                        }
                        isSearchable={true}
                        onChange={(selectedOption) =>
                          handleInputChange(
                            "class",
                            selectedOption?.value || ""
                          )
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Section</label>
                      <CommonSelect
                        className="select"
                        options={sectionOptions}
                        value={
                          sectionOptions.find(
                            (option) => option.value === formData.section
                          ) || null
                        }
                        isSearchable={true}
                        onChange={(selectedOption) =>
                          handleInputChange(
                            "section",
                            selectedOption?.value || ""
                          )
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Course</label>
                      <CommonSelect
                        className="select"
                        options={filteredSubjectOptions}
                        value={
                          filteredSubjectOptions.find(
                            (option) => option.value === formData.course
                          ) || null
                        }
                        isSearchable={true}
                        onChange={(selectedOption) =>
                          handleInputChange(
                            "course",
                            selectedOption?.value || ""
                          )
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Day</label>
                      <CommonSelect
                        className="select"
                        options={DAY_OPTIONS}
                        value={
                          DAY_OPTIONS.find(
                            (option) => option.value === formData.dayOfWeek
                          ) || null
                        }
                        isSearchable={true}
                        onChange={(selectedOption) =>
                          handleInputChange(
                            "dayOfWeek",
                            selectedOption?.value || ""
                          )
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Start Time</label>
                      <div className="date-pic">
                        <TimePicker
                          getPopupContainer={getModalContainer2}
                          use12Hours
                          placeholder="Choose"
                          format="h:mm A"
                          className="form-control timepicker"
                          value={formatTimeForDisplay(formData.startTime)}
                          onChange={(time) =>
                            handleTimeChange("startTime", time)
                          }
                          required
                        />
                        <span className="cal-icon">
                          <i className="ti ti-clock" />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">End Time</label>
                      <div className="date-pic">
                        <TimePicker
                          getPopupContainer={getModalContainer}
                          use12Hours
                          placeholder="Choose"
                          format="h:mm A"
                          className="form-control timepicker"
                          value={formatTimeForDisplay(formData.endTime)}
                          onChange={(time) => handleTimeChange("endTime", time)}
                          required
                        />
                        <span className="cal-icon">
                          <i className="ti ti-clock" />
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p>Change the Status by toggle</p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={formData.isActive}
                          onChange={(e) =>
                            handleInputChange("isActive", e.target.checked)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Add Class Routine"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Class Routine Modal */}
      <div className="modal fade" id="edit_class_routine">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Class Routine</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdateClassRoutine}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Teacher</label>
                      <CommonSelect
                        className="select"
                        options={teacherOptions}
                        value={
                          teacherOptions.find(
                            (option) => option.value === editFormData.teacher
                          ) || null
                        }
                        isSearchable={true}
                        onChange={(selectedOption) =>
                          handleEditInputChange(
                            "teacher",
                            selectedOption?.value || ""
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Class</label>
                      <CommonSelect
                        className="select"
                        options={classOptions}
                        value={
                          classOptions.find(
                            (option) => option.value === editFormData.class
                          ) || null
                        }
                        isSearchable={true}
                        onChange={(selectedOption) =>
                          handleEditInputChange(
                            "class",
                            selectedOption?.value || ""
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Section</label>
                      <CommonSelect
                        className="select"
                        options={sectionOptions}
                        value={
                          sectionOptions.find(
                            (option) => option.value === editFormData.section
                          ) || null
                        }
                        isSearchable={true}
                        onChange={(selectedOption) =>
                          handleEditInputChange(
                            "section",
                            selectedOption?.value || ""
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Course</label>
                      <CommonSelect
                        className="select"
                        options={filteredSubjectOptions}
                        value={
                          filteredSubjectOptions.find(
                            (option) => option.value === editFormData.course
                          ) || null
                        }
                        isSearchable={true}
                        onChange={(selectedOption) =>
                          handleEditInputChange(
                            "course",
                            selectedOption?.value || ""
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Day</label>
                      <CommonSelect
                        className="select"
                        options={DAY_OPTIONS}
                        value={
                          DAY_OPTIONS.find(
                            (option) => option.value === editFormData.dayOfWeek
                          ) || null
                        }
                        isSearchable={true}
                        onChange={(selectedOption) =>
                          handleEditInputChange(
                            "dayOfWeek",
                            selectedOption?.value || ""
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Start Time</label>
                      <div className="date-pic">
                        <TimePicker
                          getPopupContainer={getModalContainer3}
                          use12Hours
                          placeholder="Choose"
                          format="h:mm A"
                          className="form-control timepicker"
                          value={formatTimeForDisplay(
                            editFormData.startTime || ""
                          )}
                          onChange={(time) =>
                            handleEditTimeChange("startTime", time)
                          }
                        />
                        <span className="cal-icon">
                          <i className="ti ti-clock" />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">End Time</label>
                      <div className="date-pic">
                        <TimePicker
                          getPopupContainer={getModalContainer4}
                          use12Hours
                          placeholder="Choose"
                          format="h:mm A"
                          className="form-control timepicker"
                          value={formatTimeForDisplay(
                            editFormData.endTime || ""
                          )}
                          onChange={(time) =>
                            handleEditTimeChange("endTime", time)
                          }
                        />
                        <span className="cal-icon">
                          <i className="ti ti-clock" />
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p>Change the Status by toggle</p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={editFormData.isActive ?? true}
                          onChange={(e) =>
                            handleEditInputChange("isActive", e.target.checked)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form>
              <div className="modal-body text-center">
                <span className="delete-icon">
                  <i className="ti ti-trash-x" />
                </span>
                <h4>Confirm Deletion</h4>
                <p>
                  Are you sure you want to delete this class routine? This
                  action cannot be undone.
                </p>
                <div className="d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteClassRoutine}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassRoutineManagement;
